import React from 'react';
import './WorkSection.css';
import { useSpring, animated, useTrail } from '@react-spring/web';
import { useInView } from 'react-intersection-observer';
import { useState } from 'react';

// Import the images
import SZAMainImage from './CardContent/SZAPortal/SZAMAin.png';
import Image1 from './CardContent/SZAPortal/1.png';
import Image2 from './CardContent/SZAPortal/2.png';
import Image3 from './CardContent/SZAPortal/3.png';
import Image4 from './CardContent/SZAPortal/4.png';
import Image5 from './CardContent/SZAPortal/5.png';
import Image6 from './CardContent/SZAPortal/6.png';

import Health from './CardContent/SZAPortal/Health.png';
import Omics from './CardContent/SZAPortal/Omics.png';
import AI from './CardContent/SZAPortal/AI.png';

import NewGenome from './CardContent/SZAPortal/NewGenome.png';
import OncoRisk from './CardContent/SZAPortal/OncoRisk.png';


import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDotCircle, faGripHorizontal, faGripLines, faGripVertical, faListDots, faArrowRight, faChevronLeft, faChevronRight, faCircle, faDot } from '@fortawesome/free-solid-svg-icons';

const WorkItem = ({ number, category, title, description, content, index }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const itemSpring = useSpring({
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0px)' : 'translateY(60px)',
    config: { tension: 280, friction: 60 },
    delay: index * 200,
  });

  return (
    <animated.div ref={ref} style={itemSpring} className="ULT-work-item">
      <div className="ULT-work-item-header">
        <div className="ULT-work-number-section">
          <div className="ULT-work-number">{number}</div>
          <div className="ULT-work-category">{category}</div>
        </div>
        
        <div className="ULT-work-main-content">
          {title}
        </div>
        
        <div className="ULT-work-description">
          {description}
        </div>
      </div>
      
      {/* Custom Content Section for each work item */}
      <div className="ULT-work-item-content">
        {content}
      </div>
    </animated.div>
  );
};

// Function to generate dot pattern
const generateDotPattern = (cols, rows) => {
  const dotRows = [];
  for (let i = 0; i < rows; i++) {
    const dots = [];
    for (let j = 0; j < cols; j++) {
      dots.push(<span key={j} className="ULT-dot"></span>);
    }
    dotRows.push(
      <div key={i} className="ULT-dot-row">
        {dots}
      </div>
    );
  }
  return dotRows;
};

const PlatformGrid = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const cards = [
    {
      id: 'neural-core',
      className: 'ULT-grid-card ULT-card-2x2 ULT-simple-card',
      image: SZAMainImage,
      title: 'Project Management',
      description: "Comprehensive project management with intelligent automation of infrastructure workflows and real-time decision support."    },
    {
      id: 'platform-suite',
      className: 'ULT-grid-card ULT-card-1x2 ULT-platform-suite-card',
      isPlatformSuite: true
    },
    {
      id: 'design-engine',
      className: 'ULT-grid-card ULT-card-1x1 ULT-simple-card',
      image: Image1,
      title: 'Patient Observation',
      description: "Real-time patient tracking and observation tools designed to streamline monitoring, data collection, and clinical insights."









    },
    {
      id: 'neural-net',
      className: 'ULT-grid-card ULT-card-1x1 ULT-simple-card',
      image: Image2,
      title: 'Patient and Cohort Managment',
      description: "Tools for managing individual patients and cohorts with integrated invitations, scheduling, and automated data organization."    },
    {
      id: 'shield-core',
      className: 'ULT-grid-card ULT-card-1x1 ULT-simple-card',
      image: Image3,
      title: 'In-House Sample Handling',
      description: "Secure and efficient in-house sample intake, processing, and tracking designed to ensure full control and traceability."
    },
    {
      id: 'data-flow',
      className: 'ULT-grid-card ULT-card-1x1 ULT-simple-card',
      image: Image4,
  title: 'Centralized Report Generation and Multilingual Delivery',
  description: 'Automated reporting pipeline with centralized control, supporting multilingual output and seamless distribution.'
    },

    {
      id: 'monitor',
      className: 'ULT-grid-card ULT-card-1x1 ULT-simple-card',
      image: Image6,
      title: 'Cloud Pipeline Processing',
      description: 'Automated pipelines that manage data processing, result sorting, and patient communication through a seamless cloud system.'
    },
    {
      id: 'api-gateway',
      className: 'ULT-grid-card ULT-card-1x1 ULT-simple-card',
      image: Image5,
      title: 'AI Omics Atlas',
      description: 'A multi-agent system that generates specialized agents for in-house multi-omics analysis, identifies novel discoveries in real time using clinical data, and visualizes the reasoning behind each result through live pipeline execution.'
    },
  ];

  const trail = useTrail(cards.length, {
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0px) scale(1)' : 'translateY(40px) scale(0.95)',
    config: { tension: 280, friction: 60 },
    delay: 300,
  });

  return (
    <div ref={ref} className="ULT-platform-content">
      <div className="ULT-platform-grid">
        {trail.map((style, index) => {
          const card = cards[index];
          return (
            <animated.div key={card.id} style={style} className={card.className}>
              {card.isPlatformSuite ? (
                // Platform Suite Card
                <>
              <div className="ULT-card-background"></div>
<div className="ULT-card-content ULT-platform-suite-content">
  <div className="ULT-product-lineup">Platform Overview </div>
  
  <h2 className="ULT-platform-title">
    End-to-End Research<br/>
    Management and Sample<br/>
    Processing Infrastructure
  </h2>
  
  <p className="ULT-platform-description">
    A streamlined suite for managing projects, tracking samples, and delivering reports—enhanced by intelligent AI support for Omics Discoveries.
  </p>
  
  <div className="ULT-dot-pattern">
    {generateDotPattern(12, 13)}
  </div>
</div>

                </>
              ) : (
                // Simple Cards
                <>
                  <div className="ULT-card-background" style={{ 
                    backgroundImage: `url(${card.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}></div>
                  <div className="ULT-card-icon"><FontAwesomeIcon icon={faGripHorizontal} /></div>
                  <div className="ULT-card-content">
                    <h3>{card.title}</h3>
                  </div>
                  <div className="ULT-card-description">
                    {card.description}
                  </div>
                  <div className="ULT-card-button">
                    <FontAwesomeIcon icon={faArrowRight} />
                  </div>
                </>
              )}
            </animated.div>
          );
        })}
      </div>
    </div>
  );
};

// New Carousel Section Component
const CarouselSection = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Sample carousel data - you can modify this with your actual content
  const pages = [

    
    {
      id: 0,
      leftContent: {
        image: Omics,
      },
      stepTitle: "Omics Insights and Reports",
      stepDescription: "View detailed multi-omics data processed through intelligent pipelines, with personalized PDF reports delivered in seven languages for global accessibility and clinical clarity.",
      bottomTitle: "Multi-Language Report Delivery",
      bottomSubtitle: "7-language support, personalized data"
    }
    ,
    {
      id: 1,
      leftContent: {
        image: Health,
      },
      stepTitle: "Seamless Wearable Tracking",
      stepDescription: "Track health metrics in real time with full support for Apple and Android wearables. Capture nutrition by snapping a photo, logging manually, or speaking directly to your AI assistant—all integrated into a continuous, personalized health feedback loop.",
      bottomTitle: "Compatible with 150+ Wearables",
      bottomSubtitle: "Realtime data, passive syncing"
    },
    {
      id: 2,
      leftContent: {
        image: Health,
      },
      stepTitle: "Cognitive Engagement",
      stepDescription: "Access a suite of scientifically designed cognitive games that adapt to your performance, helping monitor mental sharpness and support brain health as part of your daily routine.",
      bottomTitle: "Built-In Brain Training",
      bottomSubtitle: "Adaptive, science-backed games"
    }
    ,
    {
      id: 3,
      leftContent: {
        image: AI,
      },
      stepTitle: "Longevity AI Integration",
      stepDescription: "Interact with your AI assistant through voice or text to explore your omics results, receive real-time feedback, and understand how your wearable data, nutrition, and lifestyle all connect in a personalized health narrative.",
      bottomTitle: "Conversational Health Intelligence",
      bottomSubtitle: "AI-driven feedback and insights"
    }
    
  ];

  const handlePrevious = () => {
    setCurrentPage((prev) => (prev === 0 ? pages.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => (prev === pages.length - 1 ? 0 : prev + 1));
  };

  const handleDotClick = (index) => {
    setCurrentPage(index);
  };

  const currentPageData = pages[currentPage];

  // Animation for content transitions
  const contentSpring = useSpring({
    opacity: 1,
    transform: 'translateX(0px)',
    from: { opacity: 0, transform: 'translateX(20px)' },
    reset: true,
    config: { tension: 280, friction: 60 }
  });

  const containerSpring = useSpring({
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0px)' : 'translateY(60px)',
    config: { tension: 280, friction: 60 },
    delay: 200,
  });

  return (
    <animated.div ref={ref} style={containerSpring} className="ULT-carousel-section">
      <div className="ULT-carousel-grid">
        {/* Left side - 4x2 */}
               {/* Left side - 4x2 */}
               <animated.div style={contentSpring} className="ULT-carousel-left ULT-grid-4x2">
          <img 
            src={currentPageData.leftContent.image} 
            alt={currentPageData.stepTitle} 
            className="ULT-carousel-left-image" 
          />
          <div className="ULT-carousel-left-content">
            <h2>{currentPageData.leftContent.title}</h2>
            <h3>{currentPageData.leftContent.subtitle}</h3>
            <p>{currentPageData.leftContent.description}</p>
          </div>
        </animated.div>
        {/* Top middle - 1x4 with title and progress dots */}
        <div className="ULT-carousel-top-middle ULT-grid-1x4">
          <div className="ULT-carousel-title">
            <h3>Longevity App</h3>
          </div>
          <div className="ULT-progress-dots">
            {pages.map((_, index) => (
              <button
                key={index}
                className={`ULT-progress-dot ${index === currentPage ? 'active' : ''}`}
                onClick={() => handleDotClick(index)}
              >
                <FontAwesomeIcon icon={index === currentPage ? faCircle : faDotCircle} />
              </button>
            ))}
          </div>
        </div>

        {/* Top right - 1x1 with navigation buttons */}
        <div className="ULT-carousel-nav ULT-grid-1x1">
          <button className="ULT-nav-btn ULT-nav-prev" onClick={handlePrevious}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <button className="ULT-nav-btn ULT-nav-next" onClick={handleNext}>
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>

        {/* Bottom - 2x5 with step content */}
        <animated.div style={contentSpring} className="ULT-carousel-bottom ULT-grid-2x5">
          <div className="ULT-carousel-step-content">
            <div className="ULT-step-header">
              <h2>{currentPageData.stepTitle}</h2>
              <p className="ULT-step-description">{currentPageData.stepDescription}</p>
            </div>
            <div className="ULT-step-footer">
              <h4 className="ULT-bottom-title">{currentPageData.bottomTitle}</h4>
              <p className="ULT-bottom-subtitle">{currentPageData.bottomSubtitle}</p>
            </div>
          </div>
        </animated.div>
      </div>
    </animated.div>
  );
};

// Simple Glassmorphism Section - Content Right
const GlassSectionRight = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerSpring = useSpring({
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0px)' : 'translateY(40px)',
    config: { tension: 280, friction: 60 },
    delay: 200,
  });

  return (
    <animated.div ref={ref} style={containerSpring} className="ULT-glass-section">
      <div className="ULT-glass-container ULT-content-right">
        <div className="ULT-glass-text-content">
          <div className="ULT-glass-layer ULT-glass-layer-1">
          <h2 className="ULT-glass-title">Comprehensive Oncology Platform</h2>

          </div>
          <div className="ULT-glass-layer ULT-glass-layer-2">
          <h3 className="ULT-glass-subtitle">Data Exploration, Analysis, and Reporting</h3>
          </div>
          <div className="ULT-glass-layer ULT-glass-layer-3">
          <p className="ULT-glass-description">
  Access one of the largest oncology databases combined with advanced research tools, AI-assisted exploration, 
  and automated report generation—all in a unified system.
</p>

          </div>
        </div>
        <div className="ULT-glass-content-tile">
          <div className="ULT-content-tile-background" style={{
            backgroundImage: `url(${OncoRisk})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}></div>
          <div className="ULT-content-tile-overlay">
            <div className="ULT-tile-content">
              <div className="ULT-tile-icon">
                <FontAwesomeIcon icon={faGripVertical} />
              </div>
              <h4>Real-time Processing</h4>
              <p>Instant data analysis and response</p>
            </div>
          </div>
        </div>
      </div>
    </animated.div>
  );
};

// Simple Glassmorphism Section - Content Left
const GlassSectionLeft = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerSpring = useSpring({
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0px)' : 'translateY(40px)',
    config: { tension: 280, friction: 60 },
    delay: 200,
  });

  return (
    <animated.div ref={ref} style={containerSpring} className="ULT-glass-section">
      <div className="ULT-glass-container ULT-content-left">
        <div className="ULT-glass-content-tile">
          <div className="ULT-content-tile-background" style={{
            backgroundImage: `url(${NewGenome})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}></div>
         <div className="ULT-content-tile-overlay">
  <div className="ULT-tile-content">
    <div className="ULT-tile-icon">
      <FontAwesomeIcon icon={faGripLines} />
    </div>
    <h4>Genomic Data Exploration</h4>
    <p>VCF & query-based workflows</p>
  </div>
</div>
</div>
<div className="ULT-glass-text-content">
  <div className="ULT-glass-layer ULT-glass-layer-1">
    <h2 className="ULT-glass-title">Comprehensive Genomics Platform</h2>
  </div>
  <div className="ULT-glass-layer ULT-glass-layer-2">
    <h3 className="ULT-glass-subtitle">Exploration, Visualization, and Reporting</h3>
  </div>
  <div className="ULT-glass-layer ULT-glass-layer-3">
    <p className="ULT-glass-description">
      Explore and visualize genomic data from large databases with query-driven workflows and generate detailed reports—all in one system.
    </p>
  </div>

        </div>
      </div>
    </animated.div>
  );
};

const WorkSection = () => {
  const [titleRef, titleInView] = useInView({
    triggerOnce: true,
    threshold: 0.5,
  });

  const titleSpring = useSpring({
    opacity: titleInView ? 1 : 0,
    transform: titleInView ? 'translateY(0px)' : 'translateY(30px)',
    config: { tension: 280, friction: 60 },
  });

  const workItems = [
    {
      number: "001",
      category: "SZA Portal",
      title: "A comprehensive platform for end-to-end project and infrastructure management, sample processing and tracking, automated report delivery, and integration of multi-agent AI systems.",
      description: "Designed for seamless in-house integrations of all services. From data handling, analysis and omics discovery to intelligent report delivery.",
      content: <PlatformGrid />
    },
{
  number: "002", 
  category: "SZA Longevity App",
  title: "Patient-facing app for omics insights, clinical reporting, and AI interaction.",
  description: "Explore your omics results, receive clinical insights, and communicate directly with your AI—all in one streamlined interface.",
  content: <CarouselSection />
},

{
  number: "003",
  category: "OncoRisk",
  title: "Comprehensive Oncology Platform for Data Exploration, Analysis, and Reporting",
  description: "Access one of the largest oncology databases combined with advanced research tools, AI-assisted exploration, and automated report generation—all in a unified system.",
  content: <GlassSectionRight />
},

    {
      number: "004",
      category: "NewGenome",
      title: "VCF & Query-Based Genomics Platform for Exploration and Reporting",
      description: "Explore and visualize genomic data from large databases with query-driven workflows and generate detailed reports—all in one system.",
      
      content: <GlassSectionLeft />
    },

  ];

  return (
    <section className="ULTHOME-work-section">
      <animated.h2 ref={titleRef} style={titleSpring} className="ULT-ourwork">Our Work</animated.h2>
      <div className="ULT-work-items-container">
        {workItems.map((item, index) => (
          <WorkItem 
            key={index}
            index={index}
            number={item.number}
            category={item.category}
            title={item.title}
            description={item.description}
            content={item.content}
          />
        ))}
      </div>
    </section>
  );
};

export default WorkSection; 